import { useEffect, useRef } from 'react';
import {
  setAnalyticsUser,
  trackView,
  trackLeave,
  trackNav,
  flushAnalytics,
} from '@/lib/analytics/track';

/**
 * Page/behaviour analytics hook (8 JUN 2026). Mount once in the authed shell.
 *
 * Emits, as `screen`/`detail` change:
 *  - `page_view` for the new screen,
 *  - `nav` (from → to),
 *  - `page_leave` with the dwell (ms) spent on the previous screen.
 *
 * Dwell is segment-based: a segment starts when a screen becomes visible and
 * ends on screen change OR tab hide (`visibilitychange`/`pagehide`), so time
 * spent reading is captured even when the user switches tabs, and a hidden
 * gap is never counted as on-page time.
 */
export function usePageAnalytics(
  screen: string,
  detail: string | null,
  userId: string | null,
): void {
  // Bind/unbind the analytics identity (handles login / logout / cross-tab swap).
  useEffect(() => {
    setAnalyticsUser(userId);
  }, [userId]);

  // Current (screen, detail) + the open dwell segment start (null while hidden).
  const currentRef = useRef<{ screen: string; detail: string | null } | null>(null);
  const segmentStartRef = useRef<number | null>(null);

  // Track screen/detail transitions.
  useEffect(() => {
    if (!userId) return;
    const now = Date.now();
    const prev = currentRef.current;
    if (prev) {
      if (segmentStartRef.current != null) {
        trackLeave(prev.screen, prev.detail, now - segmentStartRef.current);
      }
      if (prev.screen !== screen) trackNav(prev.screen, screen, detail);
    }
    trackView(screen, detail);
    currentRef.current = { screen, detail };
    segmentStartRef.current = now;
    // userId intentionally omitted: identity is handled in the effect above;
    // re-running here on a swap would double-emit. Screen/detail drive views.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, detail]);

  // Pause/resume dwell on tab visibility; flush on hide.
  useEffect(() => {
    const closeSegment = () => {
      const cur = currentRef.current;
      if (cur && segmentStartRef.current != null) {
        trackLeave(cur.screen, cur.detail, Date.now() - segmentStartRef.current);
      }
      segmentStartRef.current = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        closeSegment();
        void flushAnalytics();
      } else if (currentRef.current) {
        segmentStartRef.current = Date.now(); // resume timing for the same screen
      }
    };
    const onPageHide = () => {
      closeSegment();
      void flushAnalytics();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      closeSegment();
      void flushAnalytics();
    };
  }, []);
}
